var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useContext, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { findSnapTargetManualTile, trimRandomEmptyEdgeRowsAndCols, trimManualEmptyEdgeRowsAndCols, compactEmptyInteriorRowsAndCols, } from '../helper/helper';
import { TILE_COLORS } from '../widgets/ColoredTiles';
import { saveState } from '../helper/stateManagerHelper';
import globalStore from '../store';
var ColoredTileController = function () {
    var _a = useContext(DataContext), manipulative = _a.manipulative, setManipulative = _a.setManipulative, setSelectedTool = _a.setSelectedTool, setSelectedManipulativeId = _a.setSelectedManipulativeId, getPositionForNewItem = _a.getPositionForNewItem, setContextMenu = _a.setContextMenu;
    var playerBgPattern = globalStore.useStoreState(function (store) { return store.playerStore; }).playerBgPattern;
    // Always read the latest manipulative inside event handlers — avoids the stale-closure
    // race where the listener fires with a manipulative array captured before a recent
    // group/ungroup update propagated.
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    useEffect(function () {
        var handleEvents = function (data) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
            var newStateToSave = [];
            switch (data.id) {
                case 'createColoredTile': {
                    var _y = data.data, tileColor = _y.color, x = _y.x, y = _y.y, sourceId_1 = _y.sourceId, isInvisible = _y.isInvisible, sourceLatestData_1 = _y.sourceLatestData;
                    var cellSize = 110;
                    // The dragged cell's visual size tracks the source tile's scale, so snap
                    // detection must use a scale-matched drag box (else a scaled tile resolves
                    // the wrong target row/col and drops the cell in the wrong place).
                    var dragScale = (_c = (_b = (_a = data.data) === null || _a === void 0 ? void 0 : _a.scale) !== null && _b !== void 0 ? _b : sourceLatestData_1 === null || sourceLatestData_1 === void 0 ? void 0 : sourceLatestData_1.scale) !== null && _c !== void 0 ? _c : 1;
                    var scaledCell = cellSize * dragScale;
                    // EXPLICIT TARGET (outline-fill from the right-panel click): the caller
                    // already knows the exact tile + cell to fill, so bypass position-based
                    // snap re-detection (which can resolve to a different tile and make the
                    // cell "drop in another place"). Fill that tile's (row,col) directly.
                    var _z = data.data, targetTileId_1 = _z.targetTileId, targetRow = _z.targetRow, targetCol = _z.targetCol;
                    if (targetTileId_1 != null && targetRow != null && targetCol != null) {
                        var targetItem = manipulativeRef.current.find(function (m) { return m.data.id === targetTileId_1; });
                        var td = targetItem === null || targetItem === void 0 ? void 0 : targetItem.data;
                        if (!td || td.type !== 'coloredTiles') {
                            break;
                        }
                        var existing = (_d = td.partColors) === null || _d === void 0 ? void 0 : _d["".concat(targetRow, "-").concat(targetCol)];
                        var occupied = (td.generationMode === 'random' && existing !== 'transparent') ||
                            ((td.generationMode === 'manual' || td.generationMode === 'outline') && !!existing);
                        if (occupied) {
                            break;
                        }
                        var oldData = structuredClone(td);
                        var newData_1 = structuredClone(td);
                        if (!newData_1.partColors)
                            newData_1.partColors = {};
                        newData_1.partColors["".concat(targetRow, "-").concat(targetCol)] = tileColor;
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === targetTileId_1 ? __assign(__assign({}, item), { data: newData_1 }) : item;
                            });
                        });
                        saveAllChanges([
                            {
                                type: targetTileId_1,
                                id: targetTileId_1.split('_')[1],
                                propertyName: 'completeData',
                                old: JSON.stringify(oldData),
                                new: JSON.stringify(newData_1),
                            },
                        ]);
                        setSelectedTool([targetTileId_1]);
                        setSelectedManipulativeId(targetTileId_1);
                        break;
                    }
                    // The dispatcher (cell extract in ColoredTiles.tsx) runs setManipulative
                    // synchronously before this handler fires, but React hasn't committed yet —
                    // manipulativeRef.current still holds the un-trimmed source. Override that
                    // entry with `sourceLatestData` so snap detection uses the visible bounds.
                    var freshManipulative = sourceLatestData_1
                        ? manipulativeRef.current.map(function (item) {
                            return item.data.id === sourceLatestData_1.id
                                ? __assign(__assign({}, item), { data: sourceLatestData_1 }) : item;
                        })
                        : manipulativeRef.current;
                    // Find existing manual colored tiles to snap to.
                    // Threshold 40 matches the drag-preview threshold so drops merge wherever
                    // the snap ghost was visible.
                    // Invisible placeholder tiles (created when the user picks manual mode from
                    // the popup) must never snap/merge — they are just markers for the first
                    // block the user will drag, so always place them fresh.
                    var snapTarget = isInvisible ? null : findSnapTargetManualTile({ x: x, y: y }, { width: scaledCell, height: scaledCell }, freshManipulative, undefined, 40, cellSize, 'manual', dragScale);
                    if (snapTarget) {
                        var _0 = snapTarget, targetItem = _0.item, mergeDir = _0.dir, offset_1 = _0.offset, row = _0.row, col = _0.col;
                        var et = targetItem.data;
                        // The merged tile keeps the target's scale, so center shifts when adding
                        // a cell must use the target's scaled cell size (not raw 110px).
                        var targetScaledCell = cellSize * ((_e = et.scale) !== null && _e !== void 0 ? _e : 1);
                        if (mergeDir) {
                            var oldData = structuredClone(et);
                            var newData = structuredClone(et);
                            if (mergeDir === 'RIGHT') {
                                newData.columns += 1;
                                newData.center.x += targetScaledCell / 2;
                                if (!newData.partColors)
                                    newData.partColors = {};
                                if (newData.generationMode === 'random') {
                                    // Explicitize existing default colors
                                    for (var r_1 = 0; r_1 < newData.rows; r_1++) {
                                        for (var c = 0; c < newData.columns - 1; c++) {
                                            if (!newData.partColors["".concat(r_1, "-").concat(c)]) {
                                                newData.partColors["".concat(r_1, "-").concat(c)] = TILE_COLORS[(r_1 * 2 + c) % 4];
                                            }
                                        }
                                    }
                                    for (var i = 0; i < newData.rows; i++) {
                                        newData.partColors["".concat(i, "-").concat(newData.columns - 1)] = 'transparent';
                                    }
                                }
                                // Handle vertical offset in manual/outline mode
                                if (offset_1 !== 0 && (newData.generationMode === 'manual' || newData.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        newData.rows = Math.max(newData.rows, 1 + offset_1);
                                    }
                                    else {
                                        var shiftY_1 = Math.abs(offset_1);
                                        newData.rows += shiftY_1;
                                        newData.center.y -= (shiftY_1 * targetScaledCell) / 2;
                                        var shifted_1 = {};
                                        Object.entries(newData.partColors || {}).forEach(function (_a) {
                                            var k = _a[0], v = _a[1];
                                            var _b = k.split('-').map(Number), r = _b[0], c = _b[1];
                                            shifted_1["".concat(r + shiftY_1, "-").concat(c)] = v;
                                        });
                                        newData.partColors = shifted_1;
                                        if (newData.leftLinearSegments)
                                            newData.leftLinearSegments = newData.leftLinearSegments.map(function (r) { return r + shiftY_1; });
                                        if (newData.cutRows)
                                            newData.cutRows = newData.cutRows.map(function (r) { return r + shiftY_1; });
                                    }
                                }
                                var r = offset_1 < 0 ? 0 : offset_1;
                                newData.partColors["".concat(r, "-").concat(newData.columns - 1)] = tileColor;
                                // Transfer linear pieces
                                if (data.data.topLinearSegments && data.data.topLinearSegments.length > 0) {
                                    newData.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.topLinearSegments || []), true), [newData.columns - 1], false)));
                                }
                                if (data.data.leftLinearSegments && data.data.leftLinearSegments.length > 0) {
                                    newData.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.leftLinearSegments || []), true), [r], false)));
                                }
                            }
                            else if (mergeDir === 'LEFT') {
                                newData.columns += 1;
                                newData.center.x -= targetScaledCell / 2;
                                // Shift existing colors
                                var shiftedColors_1 = {};
                                if (newData.generationMode === 'random') {
                                    // Explicitize existing default colors so they don't change when coordinates shift
                                    for (var r_2 = 0; r_2 < newData.rows; r_2++) {
                                        for (var c = 0; c < newData.columns - 1; c++) {
                                            if (!((_f = newData.partColors) === null || _f === void 0 ? void 0 : _f["".concat(r_2, "-").concat(c)])) {
                                                newData.partColors = newData.partColors || {};
                                                newData.partColors["".concat(r_2, "-").concat(c)] = TILE_COLORS[(r_2 * 2 + c) % 4];
                                            }
                                        }
                                    }
                                    for (var i = 0; i < newData.rows; i++) {
                                        shiftedColors_1["".concat(i, "-0")] = 'transparent';
                                    }
                                }
                                Object.entries(newData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    shiftedColors_1["".concat(r + (offset_1 < 0 ? Math.abs(offset_1) : 0), "-").concat(c + 1)] = color;
                                });
                                // Handle vertical offset in manual/outline mode
                                if (offset_1 !== 0 && (newData.generationMode === 'manual' || newData.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        newData.rows = Math.max(newData.rows, 1 + offset_1);
                                    }
                                    else {
                                        var shiftY = Math.abs(offset_1);
                                        newData.rows += shiftY;
                                        newData.center.y -= (shiftY * targetScaledCell) / 2;
                                    }
                                }
                                var r = offset_1 > 0 ? offset_1 : 0;
                                shiftedColors_1["".concat(r, "-0")] = tileColor;
                                newData.partColors = shiftedColors_1;
                                // Transfer linear pieces
                                if (data.data.topLinearSegments && data.data.topLinearSegments.length > 0) {
                                    newData.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.topLinearSegments || []), true), [0], false)));
                                }
                                if (data.data.leftLinearSegments && data.data.leftLinearSegments.length > 0) {
                                    newData.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.leftLinearSegments || []), true), [r], false)));
                                }
                                // Shift auxiliary data
                                if (newData.cutColumns)
                                    newData.cutColumns = newData.cutColumns.map(function (v) { return v + 1; });
                                if (newData.topLinearSegments)
                                    newData.topLinearSegments = newData.topLinearSegments.map(function (v) { return v + 1; });
                                if (newData.leftLinearSegments && offset_1 < 0)
                                    newData.leftLinearSegments = newData.leftLinearSegments.map(function (r) { return r + Math.abs(offset_1); });
                                if (newData.cutRows && offset_1 < 0)
                                    newData.cutRows = newData.cutRows.map(function (r) { return r + Math.abs(offset_1); });
                                if (newData.selectedPartKeys) {
                                    newData.selectedPartKeys = newData.selectedPartKeys.map(function (k) {
                                        if (k.startsWith('cell-')) {
                                            var _a = k.replace('cell-', '').split('-').map(Number), r_3 = _a[0], c = _a[1];
                                            return "cell-".concat(r_3, "-").concat(c + 1);
                                        }
                                        if (k.startsWith('red-top-')) {
                                            var c = Number(k.replace('red-top-', ''));
                                            return "red-top-".concat(c + 1);
                                        }
                                        return k;
                                    });
                                }
                            }
                            else if (mergeDir === 'BOTTOM') {
                                newData.rows += 1;
                                newData.center.y += targetScaledCell / 2;
                                if (!newData.partColors)
                                    newData.partColors = {};
                                if (newData.generationMode === 'random') {
                                    // Explicitize existing default colors
                                    for (var r = 0; r < newData.rows - 1; r++) {
                                        for (var c_1 = 0; c_1 < newData.columns; c_1++) {
                                            if (!newData.partColors["".concat(r, "-").concat(c_1)]) {
                                                newData.partColors["".concat(r, "-").concat(c_1)] = TILE_COLORS[(r * 2 + c_1) % 4];
                                            }
                                        }
                                    }
                                    for (var i = 0; i < newData.columns; i++) {
                                        newData.partColors["".concat(newData.rows - 1, "-").concat(i)] = 'transparent';
                                    }
                                }
                                // Handle horizontal offset in manual/outline mode
                                if (offset_1 !== 0 && (newData.generationMode === 'manual' || newData.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        newData.columns = Math.max(newData.columns, 1 + offset_1);
                                    }
                                    else {
                                        var shiftX_1 = Math.abs(offset_1);
                                        newData.columns += shiftX_1;
                                        newData.center.x -= (shiftX_1 * targetScaledCell) / 2;
                                        var shifted_2 = {};
                                        Object.entries(newData.partColors || {}).forEach(function (_a) {
                                            var k = _a[0], v = _a[1];
                                            var _b = k.split('-').map(Number), r = _b[0], c = _b[1];
                                            shifted_2["".concat(r, "-").concat(c + shiftX_1)] = v;
                                        });
                                        newData.partColors = shifted_2;
                                        if (newData.topLinearSegments)
                                            newData.topLinearSegments = newData.topLinearSegments.map(function (c) { return c + shiftX_1; });
                                        if (newData.cutColumns)
                                            newData.cutColumns = newData.cutColumns.map(function (c) { return c + shiftX_1; });
                                    }
                                }
                                var c = offset_1 < 0 ? 0 : offset_1;
                                newData.partColors["".concat(newData.rows - 1, "-").concat(c)] = tileColor;
                                // Transfer linear pieces
                                if (data.data.topLinearSegments && data.data.topLinearSegments.length > 0) {
                                    newData.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.topLinearSegments || []), true), [c], false)));
                                }
                                if (data.data.leftLinearSegments && data.data.leftLinearSegments.length > 0) {
                                    newData.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.leftLinearSegments || []), true), [newData.rows - 1], false)));
                                }
                            }
                            else if (mergeDir === 'TOP') {
                                newData.rows += 1;
                                newData.center.y -= targetScaledCell / 2;
                                // Shift existing colors
                                var shiftedColors_2 = {};
                                if (newData.generationMode === 'random') {
                                    for (var i = 0; i < newData.columns; i++) {
                                        shiftedColors_2["0-".concat(i)] = 'transparent';
                                    }
                                    // Explicitize existing default colors so they don't change when coordinates shift
                                    for (var r = 0; r < newData.rows - 1; r++) {
                                        for (var c_2 = 0; c_2 < newData.columns; c_2++) {
                                            if (!((_g = newData.partColors) === null || _g === void 0 ? void 0 : _g["".concat(r, "-").concat(c_2)])) {
                                                newData.partColors = newData.partColors || {};
                                                newData.partColors["".concat(r, "-").concat(c_2)] = TILE_COLORS[(r * 2 + c_2) % 4];
                                            }
                                        }
                                    }
                                }
                                Object.entries(newData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    shiftedColors_2["".concat(r + 1, "-").concat(c + (offset_1 < 0 ? Math.abs(offset_1) : 0))] = color;
                                });
                                // Handle horizontal offset in manual/outline mode
                                if (offset_1 !== 0 && (newData.generationMode === 'manual' || newData.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        newData.columns = Math.max(newData.columns, 1 + offset_1);
                                    }
                                    else {
                                        var shiftX = Math.abs(offset_1);
                                        newData.columns += shiftX;
                                        newData.center.x -= (shiftX * targetScaledCell) / 2;
                                    }
                                }
                                var c = offset_1 > 0 ? offset_1 : 0;
                                shiftedColors_2["0-".concat(c)] = tileColor;
                                newData.partColors = shiftedColors_2;
                                // Transfer linear pieces
                                if (data.data.topLinearSegments && data.data.topLinearSegments.length > 0) {
                                    newData.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.topLinearSegments || []), true), [c], false)));
                                }
                                if (data.data.leftLinearSegments && data.data.leftLinearSegments.length > 0) {
                                    newData.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.leftLinearSegments || []), true), [0], false)));
                                }
                                // Shift auxiliary data
                                if (newData.cutRows)
                                    newData.cutRows = newData.cutRows.map(function (v) { return v + 1; });
                                if (newData.leftLinearSegments)
                                    newData.leftLinearSegments = newData.leftLinearSegments.map(function (v) { return v + 1; });
                                if (newData.topLinearSegments && offset_1 < 0)
                                    newData.topLinearSegments = newData.topLinearSegments.map(function (c) { return c + Math.abs(offset_1); });
                                if (newData.cutColumns && offset_1 < 0)
                                    newData.cutColumns = newData.cutColumns.map(function (c) { return c + Math.abs(offset_1); });
                                if (newData.selectedPartKeys) {
                                    newData.selectedPartKeys = newData.selectedPartKeys.map(function (k) {
                                        if (k.startsWith('cell-')) {
                                            var _a = k.replace('cell-', '').split('-').map(Number), r = _a[0], c_3 = _a[1];
                                            return "cell-".concat(r + 1, "-").concat(c_3);
                                        }
                                        if (k.startsWith('red-left-')) {
                                            var r = Number(k.replace('red-left-', ''));
                                            return "red-left-".concat(r + 1);
                                        }
                                        return k;
                                    });
                                }
                            }
                            else if (mergeDir === 'INSIDE') {
                                if (!newData.partColors)
                                    newData.partColors = {};
                                var existingColor = newData.partColors["".concat(row, "-").concat(col)];
                                var isFilledInRandom = newData.generationMode === 'random' && existingColor !== 'transparent';
                                var isFilledInManualOrOutline = (newData.generationMode === 'manual' || newData.generationMode === 'outline') && !!existingColor;
                                if (isFilledInRandom || isFilledInManualOrOutline) {
                                    return;
                                }
                                newData.partColors["".concat(row, "-").concat(col)] = tileColor;
                                // Transfer linear pieces (if they hit the edges)
                                if (row === 0 && data.data.topLinearSegments && data.data.topLinearSegments.length > 0) {
                                    newData.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.topLinearSegments || []), true), [col], false)));
                                }
                                if (col === 0 && data.data.leftLinearSegments && data.data.leftLinearSegments.length > 0) {
                                    newData.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData.leftLinearSegments || []), true), [row], false)));
                                }
                            }
                            // Update dimensions
                            newData.dimensions = {
                                width: newData.columns * targetScaledCell,
                                height: newData.rows * targetScaledCell,
                            };
                            var finalData = (mergeDir === 'INSIDE' && newData.generationMode === 'random') ||
                                (newData.generationMode === 'manual') // Also trim in manual mode after any merge
                                ? trimRandomEmptyEdgeRowsAndCols(newData, targetScaledCell) // trimRandom function actually handles manual too if we are careful, but wait...
                                : newData;
                            // Truly final data (after any trimming)
                            var trulyFinalData_1 = finalData;
                            if (newData.generationMode === 'manual') {
                                trulyFinalData_1 = trimManualEmptyEdgeRowsAndCols(newData, targetScaledCell);
                            }
                            // Collapse any entirely-empty column/row that sits BETWEEN the just-appended
                            // merge slot and the nearest filled cell on that side (extract → drop-adjacent
                            // → middle-gap case). Intentional gaps elsewhere in the tile are preserved.
                            trulyFinalData_1 = compactEmptyInteriorRowsAndCols(trulyFinalData_1, targetScaledCell, mergeDir);
                            var etId_1 = et.id;
                            newStateToSave.push({
                                type: etId_1,
                                id: etId_1.split('_')[1],
                                propertyName: 'completeData',
                                old: JSON.stringify(oldData),
                                new: JSON.stringify(trulyFinalData_1),
                            });
                            setManipulative(function (prev) {
                                var _a;
                                // Re-read the target's current groupId from prev (latest state) so a
                                // grouping that landed between event registration and dispatch is honored.
                                var targetInPrev = prev.find(function (m) { return m.data.id === etId_1; });
                                var currentGroupId = (_a = targetInPrev === null || targetInPrev === void 0 ? void 0 : targetInPrev.data) === null || _a === void 0 ? void 0 : _a.groupId;
                                var dataToWrite = currentGroupId
                                    ? __assign(__assign({}, trulyFinalData_1), { groupId: currentGroupId })
                                    : trulyFinalData_1;
                                return prev.map(function (item) {
                                    return item.data.id === etId_1 ? __assign(__assign({}, item), { data: dataToWrite }) : item;
                                });
                            });
                            // Prefer the group as the active selection so the user immediately sees
                            // group affordances (and the next click drags the whole group).
                            var targetLatest = manipulativeRef.current.find(function (m) { return m.data.id === etId_1; });
                            var targetGroupId = (_j = (_h = targetLatest === null || targetLatest === void 0 ? void 0 : targetLatest.data) === null || _h === void 0 ? void 0 : _h.groupId) !== null && _j !== void 0 ? _j : trulyFinalData_1.groupId;
                            setSelectedTool([targetGroupId !== null && targetGroupId !== void 0 ? targetGroupId : etId_1]);
                            saveAllChanges(newStateToSave);
                            break;
                        }
                    }
                    if (isInvisible) {
                        setTimeout(function () {
                            customEventHandler.dispatchEvent('toggleRightSidePanel', { isOpen: true });
                        }, 0);
                        // Manual mode: move keyboard focus into the right tool panel so the
                        // user can immediately pick a colour to build the tile. Retry across
                        // the drawer's slide-in (and to win over the placeholder tile's own
                        // focus). Mirrors the context-menu "Right Toolbar" focus pattern.
                        var focusRightPanel_1 = function () {
                            var target = document.querySelector('#rightSideDrawerContainer .drawer-toggle-btn');
                            target === null || target === void 0 ? void 0 : target.focus();
                        };
                        [150, 350, 600, 900].forEach(function (d) { return setTimeout(focusRightPanel_1, d); });
                    }
                    else {
                        setManipulative(function (prev) {
                            return prev.filter(function (m) { return !(m.data.type === 'coloredTiles' && m.data.isInvisible); });
                        });
                    }
                    // If no snapping occurred, create a new 1x1 item.
                    // Auto-scale ONLY when the new piece's edge is touching an existing tile's
                    // edge (i.e., it was snapped/aligned during drag). For free drops far from
                    // other tiles, keep the default scale so the new tile doesn't unexpectedly
                    // resize itself.
                    // The dropped cell is sized to the source tile's scale, so its footprint
                    // and the fresh tile must use that scale (not raw 110) — else a scaled
                    // cell snaps back to full size and a wrong position.
                    var newDefaultLeft = x;
                    var newDefaultTop = y;
                    var newDefaultRight = x + scaledCell;
                    var newDefaultBottom = y + scaledCell;
                    // E7: 6 logical px feels right at scale 1 but is too tight at scale 4 and
                    // too loose at scale 0.25. Scale the tolerance with the candidate target so
                    // the "edge touching" feel is consistent across scales.
                    var BASE_ADJACENCY_TOLERANCE = 6;
                    // Default to the dragged cell's own scale; only an adjacent tile overrides it.
                    var inheritedScale = dragScale;
                    for (var _i = 0, _1 = manipulativeRef.current; _i < _1.length; _i++) {
                        var item = _1[_i];
                        if (item.data.type !== 'coloredTiles')
                            continue;
                        var td = item.data;
                        if (td.isInvisible)
                            continue;
                        var tScale = (_k = td.scale) !== null && _k !== void 0 ? _k : 1;
                        var tCell = cellSize * tScale;
                        var tw = td.columns * tCell;
                        var th = td.rows * tCell;
                        var tl = td.center.x - tw / 2;
                        var tt = td.center.y - th / 2;
                        var tr = tl + tw;
                        var tb = tt + th;
                        var tolerance = BASE_ADJACENCY_TOLERANCE * tScale;
                        // Edge-touching check: any one of the 4 sides aligns with the existing tile.
                        var horizTouching = (Math.abs(newDefaultRight - tl) < tolerance ||
                            Math.abs(newDefaultLeft - tr) < tolerance) &&
                            newDefaultBottom > tt - tolerance &&
                            newDefaultTop < tb + tolerance;
                        var vertTouching = (Math.abs(newDefaultBottom - tt) < tolerance ||
                            Math.abs(newDefaultTop - tb) < tolerance) &&
                            newDefaultRight > tl - tolerance &&
                            newDefaultLeft < tr + tolerance;
                        if (horizTouching || vertTouching) {
                            inheritedScale = tScale;
                            break;
                        }
                    }
                    // Center the fresh tile at its resolved scale, keeping the dropped top-left (x,y).
                    var newCenterX = x + (cellSize * inheritedScale) / 2;
                    var newCenterY = y + (cellSize * inheritedScale) / 2;
                    // Inherit display toggles (Expression / Product / dimensionLabels) from the
                    // source tile when this is an extracted cell — so a new tile broken off from
                    // a merged tile keeps the same Expression/Product setting as its source.
                    var sourceTile = sourceId_1
                        ? (_l = manipulativeRef.current.find(function (m) { return m.data.id === sourceId_1; })) === null || _l === void 0 ? void 0 : _l.data
                        : undefined;
                    var inheritShowExpression = (_m = sourceTile === null || sourceTile === void 0 ? void 0 : sourceTile.showExpression) !== null && _m !== void 0 ? _m : false;
                    var inheritShowProduct = (_o = sourceTile === null || sourceTile === void 0 ? void 0 : sourceTile.showProduct) !== null && _o !== void 0 ? _o : false;
                    var inheritDimensionLabels = (_p = sourceTile === null || sourceTile === void 0 ? void 0 : sourceTile.dimensionLabels) !== null && _p !== void 0 ? _p : false;
                    var newId_1 = "coloredTiles_".concat(uuidv4().replace(/-/g, ''));
                    var newItem_1 = {
                        type: 'coloredTiles',
                        widgetId: "coloredTiles:".concat(uuidv4()),
                        data: {
                            id: newId_1,
                            type: 'coloredTiles',
                            mode: 'grid',
                            preview: false,
                            rows: 1,
                            columns: 1,
                            cellValue: 1,
                            scale: inheritedScale,
                            center: { x: newCenterX, y: newCenterY },
                            dimensions: { width: cellSize, height: cellSize },
                            lock: { positioning: false, editing: false },
                            generationMode: 'manual',
                            redLinearPieces: false,
                            dimensionLabels: inheritDimensionLabels,
                            partColors: { '0-0': tileColor },
                            partModeEnabled: false,
                            selectModeEnabled: false,
                            showExpression: inheritShowExpression,
                            showProduct: inheritShowProduct,
                            cutRows: [],
                            cutColumns: [],
                            leftLinearSegments: [],
                            topLinearSegments: [],
                            isInvisible: !!isInvisible,
                        },
                    };
                    if (!isInvisible) {
                        newStateToSave.push({
                            type: newId_1,
                            id: newId_1.split('_')[1],
                            propertyName: 'create',
                            old: JSON.stringify(newItem_1),
                        });
                    }
                    setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem_1], false); });
                    // keepSelection (right-panel "around" placement): leave the user's
                    // originally-selected tile selected so repeated clicks keep anchoring
                    // on it and fill a tidy grid — instead of selection chasing each new
                    // tile and marching diagonally across the canvas.
                    if (!((_q = data.data) === null || _q === void 0 ? void 0 : _q.keepSelection)) {
                        setSelectedTool([newId_1]);
                        setSelectedManipulativeId(newId_1);
                    }
                    saveAllChanges(newStateToSave);
                    // When this createColoredTile came from the keyboard "Unsnap" flow,
                    // open the new tile's context menu so the user can immediately act
                    // on the extracted tile. Wait one frame for React to commit the new
                    // wrapper DOM so its rect is measurable.
                    if (((_r = data.data) === null || _r === void 0 ? void 0 : _r.openContextMenuOnCreate) && !isInvisible) {
                        setTimeout(function () {
                            var wrapper = document.querySelector("[data-manipulative-id=\"".concat(newId_1, "\"]"));
                            var rect = wrapper === null || wrapper === void 0 ? void 0 : wrapper.getBoundingClientRect();
                            if (rect) {
                                setContextMenu({
                                    x: rect.right,
                                    y: rect.top,
                                    manipulativeId: newId_1,
                                });
                            }
                        }, 150);
                    }
                    break;
                }
                case 'createLinearPiece': {
                    var _2 = data.data, lpType = _2.type, x_1 = _2.x, y_1 = _2.y;
                    var isHorizontal = lpType === 'horizontal';
                    var BASE_CELL_SIZE = 110;
                    // Drop-snap halo (canvas px). Multiplied by tileScale in the edge
                    // checks below so it scales with the tile. Halo stays in canvas px so
                    // it renders proportionally larger on screen at higher canvas zoom —
                    // matches LinearPiece's drag-time snap (src/widgets/LinearPiece.tsx).
                    var SNAP_THRESHOLD = 55;
                    var newId_2 = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
                    var cx = x_1, cy = y_1, snappedScale = 1;
                    var snappedTileId_1 = null;
                    var snappedSlotIndex_1 = -1;
                    var snappedSide_1 = null;
                    var candidates_1 = [];
                    var _loop_1 = function (item) {
                        if (item.data.type !== 'coloredTiles')
                            return "continue";
                        var td = item.data;
                        var tileScale = (_s = td.scale) !== null && _s !== void 0 ? _s : 1;
                        var scaledCell = BASE_CELL_SIZE * tileScale;
                        var w = td.columns * scaledCell;
                        var h = td.rows * scaledCell;
                        var tLeft = td.center.x - w / 2;
                        var tRight = td.center.x + w / 2;
                        var tTop = td.center.y - h / 2;
                        var tBottom = td.center.y + h / 2;
                        var leftVIds = (_t = td.snappedVerticalPieceIds) !== null && _t !== void 0 ? _t : [];
                        var rightVIds = (_u = td.snappedRightVerticalPieceIds) !== null && _u !== void 0 ? _u : [];
                        var topHIds = (_v = td.snappedHorizontalPieceIds) !== null && _v !== void 0 ? _v : [];
                        var bottomHIds = (_w = td.snappedBottomHorizontalPieceIds) !== null && _w !== void 0 ? _w : [];
                        // Scale-aware threshold (canvas px). Renders proportionally larger
                        // on screen at higher canvas zoom — matches the bigger visible tile.
                        var threshold = SNAP_THRESHOLD * tileScale;
                        var push = function (sx, sy, slot, side) {
                            candidates_1.push({
                                cx: sx, cy: sy, scale: tileScale, tileId: td.id,
                                slotIndex: slot,
                                side: side,
                                dist: Math.hypot(sx - x_1, sy - y_1),
                            });
                        };
                        if (isHorizontal) {
                            if (Math.abs(y_1 - tTop) < threshold && x_1 >= tLeft - threshold && x_1 <= tRight + threshold) {
                                var col = Math.max(0, Math.min(td.columns - 1, Math.floor((x_1 - tLeft) / scaledCell)));
                                var occ = topHIds[col];
                                if (!occ || occ === newId_2) {
                                    push(tLeft + col * scaledCell + scaledCell / 2, tTop - 21 * tileScale, col, 'top');
                                }
                            }
                            if (Math.abs(y_1 - tBottom) < threshold && x_1 >= tLeft - threshold && x_1 <= tRight + threshold) {
                                var col = Math.max(0, Math.min(td.columns - 1, Math.floor((x_1 - tLeft) / scaledCell)));
                                var occ = bottomHIds[col];
                                if (!occ || occ === newId_2) {
                                    push(tLeft + col * scaledCell + scaledCell / 2, tBottom + 21 * tileScale, col, 'bottom');
                                }
                            }
                        }
                        else {
                            if (Math.abs(x_1 - tLeft) < threshold && y_1 >= tTop - threshold && y_1 <= tBottom + threshold) {
                                var row = Math.max(0, Math.min(td.rows - 1, Math.floor((y_1 - tTop) / scaledCell)));
                                var occ = leftVIds[row];
                                if (!occ || occ === newId_2) {
                                    push(tLeft - 22 * tileScale, tTop + row * scaledCell + scaledCell / 2, row, 'left');
                                }
                            }
                            if (Math.abs(x_1 - tRight) < threshold && y_1 >= tTop - threshold && y_1 <= tBottom + threshold) {
                                var row = Math.max(0, Math.min(td.rows - 1, Math.floor((y_1 - tTop) / scaledCell)));
                                var occ = rightVIds[row];
                                if (!occ || occ === newId_2) {
                                    push(tRight + 22 * tileScale, tTop + row * scaledCell + scaledCell / 2, row, 'right');
                                }
                            }
                        }
                    };
                    for (var _3 = 0, _4 = manipulativeRef.current; _3 < _4.length; _3++) {
                        var item = _4[_3];
                        _loop_1(item);
                    }
                    if (candidates_1.length > 0) {
                        candidates_1.sort(function (a, b) { return a.dist - b.dist; });
                        var best = candidates_1[0];
                        cx = best.cx;
                        cy = best.cy;
                        snappedScale = best.scale;
                        snappedTileId_1 = best.tileId;
                        snappedSlotIndex_1 = best.slotIndex;
                        snappedSide_1 = best.side;
                    }
                    var snappedTile = snappedTileId_1
                        ? manipulativeRef.current.find(function (m) { return m.data.type === 'coloredTiles' && m.data.id === snappedTileId_1; })
                        : undefined;
                    var targetGroupId_1 = (_x = snappedTile === null || snappedTile === void 0 ? void 0 : snappedTile.data) === null || _x === void 0 ? void 0 : _x.groupId;
                    var newPiece_1 = {
                        type: 'linearPiece',
                        widgetId: "linearPiece:".concat(uuidv4()),
                        data: __assign({ id: newId_2, type: 'linearPiece', orientation: isHorizontal ? 'horizontal' : 'vertical', center: { x: cx, y: cy }, scale: snappedScale, dimensions: isHorizontal ? { width: 102, height: 10 } : { width: 10, height: 102 }, lock: { positioning: false, editing: false } }, (targetGroupId_1 && {
                            groupId: targetGroupId_1,
                            groupPosition: manipulativeRef.current.filter(function (m) { return m.data.groupId === targetGroupId_1; }).length,
                            groupCenter: undefined,
                            beforeGroupScale: snappedScale,
                            groupScale: 1,
                        })),
                    };
                    newStateToSave.push({ type: newId_2, id: newId_2.split('_')[1], propertyName: 'create', old: JSON.stringify(newPiece_1) });
                    setManipulative(function (prev) {
                        var withPiece = __spreadArray(__spreadArray([], prev, true), [newPiece_1], false);
                        if (!snappedTileId_1 || snappedSlotIndex_1 < 0 || !snappedSide_1)
                            return withPiece;
                        var sideToField = {
                            left: 'snappedVerticalPieceIds',
                            right: 'snappedRightVerticalPieceIds',
                            top: 'snappedHorizontalPieceIds',
                            bottom: 'snappedBottomHorizontalPieceIds',
                        };
                        var targetField = sideToField[snappedSide_1];
                        return withPiece.map(function (item) {
                            var _a;
                            var _b;
                            if (item.data.type !== 'coloredTiles' || item.data.id !== snappedTileId_1)
                                return item;
                            var td = item.data;
                            var isVertical = snappedSide_1 === 'left' || snappedSide_1 === 'right';
                            var length = isVertical ? td.rows : td.columns;
                            var arr = __spreadArray([], ((_b = td[targetField]) !== null && _b !== void 0 ? _b : Array(length).fill(null)), true);
                            arr[snappedSlotIndex_1] = newId_2;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), (_a = {}, _a[targetField] = arr, _a)) });
                        });
                    });
                    setSelectedTool([newId_2]);
                    setSelectedManipulativeId(newId_2);
                    saveAllChanges(newStateToSave);
                    setTimeout(function () { return customEventHandler.dispatchEvent('toggleRightSidePanel', { isOpen: true }); }, 100);
                    break;
                }
            }
        };
        var saveAllChanges = function (changes) {
            if (changes.length > 0) {
                saveState(changes);
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            }
        };
        customEventHandler.addEventListener('bottomPanel', handleEvents);
        customEventHandler.addEventListener('sidebarClicked', handleEvents);
        customEventHandler.addEventListener('headerClicked', handleEvents);
        customEventHandler.addEventListener('bottomBar', handleEvents);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleEvents);
            customEventHandler.removeEventListener('sidebarClicked', handleEvents);
            customEventHandler.removeEventListener('headerClicked', handleEvents);
            customEventHandler.removeEventListener('bottomBar', handleEvents);
        };
    }, [
        manipulative,
        setManipulative,
        setSelectedTool,
        setSelectedManipulativeId,
        getPositionForNewItem,
        playerBgPattern,
    ]);
    return null;
};
export default ColoredTileController;
